import React, { memo, useCallback, useMemo, useState } from "react"

import {
  useHumanFeedbackUpdate,
  useReviewSaveErrorHandler,
  useReviewSubjectAnnotation,
} from "@/human-feedback/annotations"
import {
  useCreateReviewAnnotationTransport,
  useReviewAnnotationTransportInvalidation,
  useUpdateReviewAnnotationTransport,
} from "@/human-feedback/transport"
import type { AudioRegionReviewConfig, MetricRecord } from "@/metrics/runtime"
import { useUser } from "@clerk/nextjs"
import { Tooltip } from "@coval/ui/tooltip"
import { AudioLines } from "lucide-react"

import { logger } from "@/lib/logger"
import type { AudioRegionParams } from "@/components/audio-player/AudioPlayer"
import type { Annotation } from "@/types/api/Annotation"
import type { RunMetricOutput } from "@/types/RunMetricOutput"

const log = logger("AudioRegionReview")

// ---------------------------------------------------------------------------
// Shared utilities
// ---------------------------------------------------------------------------

interface SubvalueByTimestamp {
  start_offset: number
  end_offset: number
  category?: string
  output_type?: string
  label?: string
}

const getSubvaluesFromAnnotation = (
  annotation: Annotation | null | undefined,
): SubvalueByTimestamp[] => {
  const raw = annotation?.ground_truth_subvalues_by_timestamp
  if (raw == null) {
    return []
  }
  if (Array.isArray(raw)) {
    return raw as SubvalueByTimestamp[]
  }
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw) as SubvalueByTimestamp[]
    } catch {
      return []
    }
  }
  return []
}

const calculateReviewedScore = (
  config: AudioRegionReviewConfig,
  editedCount: number,
  durationSeconds: number | null,
  regions: AudioRegionParams[],
): number => {
  switch (config.scoreType) {
    case "per_minute":
      if (durationSeconds == null || durationSeconds <= 0) {
        return editedCount
      }
      return (editedCount / durationSeconds) * 60.0
    case "raw_count":
      return editedCount
    case "seconds":
      return regions.reduce(
        (sum, r) => sum + ((r.end ?? r.start + 0.05) - r.start),
        0,
      )
    case "milliseconds":
      return (
        regions.reduce(
          (sum, r) => sum + ((r.end ?? r.start + 0.05) - r.start),
          0,
        ) * 1000
      )
    case "mean_seconds":
      if (regions.length === 0) {
        return 0
      }
      return (
        regions.reduce(
          (sum, r) => sum + ((r.end ?? r.start + 0.05) - r.start),
          0,
        ) / regions.length
      )
  }
}

// ---------------------------------------------------------------------------
// No-op return value when config is null
// ---------------------------------------------------------------------------

const NOOP_SAVE = () => Promise.resolve(true)
const EMPTY_REGIONS: AudioRegionParams[] = []

// ---------------------------------------------------------------------------
// Generic hook
// ---------------------------------------------------------------------------

export const useAudioRegionReview = (
  metricId: string,
  simulationOutputId: string,
  annotationsForMetric: Annotation[],
  projectId: string | undefined,
  metricOutput: RunMetricOutput | undefined,
  getAudioRegions: (mo: RunMetricOutput) => AudioRegionParams[],
  config: AudioRegionReviewConfig | null,
  durationSeconds: number | null,
  currentRegionsOverride?: AudioRegionParams[] | null,
  /**
   * The email of the user who actually owns the annotation row backing the
   * displayed annotation. In collaborative projects the displayed annotation
   * may be spoofed as the current user but actually belong to a different
   * reviewer; pass this so the save path knows to snapshot-create instead of
   * updating in place.
   */
  actualOwnerEmail?: string | null,
  onSaveStarted?: () => (succeeded?: boolean) => void,
  subjectAnnotation?: Annotation | null,
) => {
  const { user } = useUser()
  const currentUserEmail = user?.emailAddresses?.[0]?.emailAddress ?? null

  const myAnnotation = useMemo(
    () =>
      subjectAnnotation !== undefined
        ? (subjectAnnotation ?? undefined)
        : annotationsForMetric?.find(
            (annotation: Annotation) =>
              annotation.assignee === currentUserEmail,
          ),
    [annotationsForMetric, currentUserEmail, subjectAnnotation],
  )

  const { currentAnnotation, isUpdating, syncAnnotationFromServer } =
    useHumanFeedbackUpdate({
      simulationOutputId,
      metricId,
      initialAnnotation: myAnnotation || null,
      projectId,
      actualOwnerEmail,
    })

  const invalidateAnnotationCaches = useReviewAnnotationTransportInvalidation()
  const onSaveError = useReviewSaveErrorHandler()

  const updateAnnotation = useUpdateReviewAnnotationTransport({
    onSuccess: (updatedAnnotation) => {
      if (updatedAnnotation) {
        syncAnnotationFromServer(updatedAnnotation)
      }
      void invalidateAnnotationCaches(simulationOutputId, projectId)
    },
  })

  const createAnnotation = useCreateReviewAnnotationTransport({
    onSuccess: (newAnnotation) => {
      if (newAnnotation) {
        syncAnnotationFromServer(newAnnotation)
      }
      void invalidateAnnotationCaches(simulationOutputId, projectId)
    },
  })

  const [localOperationInProgress, setLocalOperationInProgress] =
    useState(false)

  const audioRegions = useMemo(() => {
    if (!metricOutput) {
      return []
    }
    return getAudioRegions(metricOutput)
  }, [metricOutput, getAudioRegions])

  const savedRegionsForMetric = useMemo((): AudioRegionParams[] => {
    if (!config) {
      return EMPTY_REGIONS
    }
    const existingSubvalues = getSubvaluesFromAnnotation(currentAnnotation)
    const filtered = existingSubvalues.filter(
      (s) =>
        s.category === config.category || s.output_type === config.category,
    )

    return filtered.map((item, idx) => {
      const labelColor = config.regionLabels?.find(
        (l) => l.value === item.label,
      )?.color
      return {
        id: `saved:${config.category}:${idx}`,
        start: item.start_offset,
        end: Math.max(
          item.end_offset ?? item.start_offset + 0.05,
          item.start_offset + 0.05,
        ),
        ...(labelColor != null && { color: labelColor }),
      }
    })
  }, [currentAnnotation, config])

  const handleSaveRegions = useCallback(
    async (
      regionsAtSaveTime?: AudioRegionParams[] | null,
      overrideFloatValue?: number,
    ) => {
      if (!config) {
        return true
      }
      if (!simulationOutputId || !metricId) {
        return false
      }
      if (localOperationInProgress) {
        return true
      }

      setLocalOperationInProgress(true)
      const endSave = onSaveStarted?.()
      let saveSucceeded = false
      const regionsToSave =
        regionsAtSaveTime ?? currentRegionsOverride ?? audioRegions

      // Zero-length markers (from editable audio player) lose their end times.
      // Restore original end times by matching markers to the AI-detected regions.
      const MATCH_EPSILON = 0.05
      const regionsWithDurations = regionsToSave.map((r) => {
        if (r.end != null && r.end !== r.start) {
          return r
        }
        const original = audioRegions.find(
          (o) => Math.abs(o.start - r.start) < MATCH_EPSILON,
        )
        return original?.end != null
          ? { ...r, end: original.end }
          : { ...r, end: r.start + 0.05 }
      })

      const editedSubvalues: SubvalueByTimestamp[] = regionsWithDurations.map(
        (r) => {
          const matchedLabel = config.regionLabels?.find(
            (l) => r.color != null && r.color === l.color,
          )
          return {
            start_offset: r.start,
            end_offset: r.end ?? r.start + 0.05,
            category: config.category,
            ...(matchedLabel != null && { label: matchedLabel.value }),
          }
        },
      )

      const existingSubvalues = getSubvaluesFromAnnotation(currentAnnotation)
      const otherCategorySubvalues = existingSubvalues.filter(
        (s) =>
          s.category !== config.category && s.output_type !== config.category,
      )
      const mergedSubvalues = [...otherCategorySubvalues, ...editedSubvalues]

      const groundTruthFloatValue =
        overrideFloatValue ??
        calculateReviewedScore(
          config,
          editedSubvalues.length,
          durationSeconds,
          regionsWithDurations,
        )
      const reviewedResultJson = config.buildReviewedResultJson({
        editedCount: editedSubvalues.length,
        score: groundTruthFloatValue,
        durationSeconds,
        metricOutput,
        regions: regionsWithDurations,
      })

      const hasPersistedRow =
        !!currentAnnotation?.id && !currentAnnotation.id.startsWith("temp-")
      const ownerEmail =
        actualOwnerEmail !== undefined
          ? actualOwnerEmail
          : (currentAnnotation?.assignee ?? null)
      const isMine = !!currentUserEmail && ownerEmail === currentUserEmail

      try {
        if (hasPersistedRow && isMine) {
          await updateAnnotation.mutateAsync({
            annotationId: currentAnnotation.id,
            groundTruthFloatValue,
            groundTruthStringValue: currentAnnotation.ground_truth_string_value,
            groundTruthSetValue: currentAnnotation.ground_truth_set_value,
            groundTruthSubvaluesByTimestamp: mergedSubvalues,
            groundTruthJson: reviewedResultJson,
            ...(projectId !== undefined ? { projectId } : {}),
          })
        } else {
          if (!currentUserEmail) {
            throw new Error(
              "Cannot create annotation without a current user email",
            )
          }

          // Snapshot-on-edit: in collaborative projects, when the displayed
          // annotation actually belongs to another reviewer, create a new
          // annotation row owned by the current user that carries forward
          // the unchanged fields from the prior row.
          await createAnnotation.mutateAsync({
            simulationOutputId,
            metricId,
            assignee: currentUserEmail,
            ground_truth_float_value: groundTruthFloatValue,
            ground_truth_string_value:
              currentAnnotation?.ground_truth_string_value ?? null,
            ground_truth_set_value:
              currentAnnotation?.ground_truth_set_value ?? null,
            ground_truth_subvalues_by_timestamp: mergedSubvalues,
            ground_truth_json: reviewedResultJson,
            annotations: currentAnnotation?.annotations ?? null,
            ...(projectId !== undefined ? { projectId } : {}),
          })
        }
        saveSucceeded = true
        return true
      } catch (error) {
        log.error("Unexpected error in audio region save", error)
        onSaveError?.(simulationOutputId, error)
        return false
      } finally {
        endSave?.(saveSucceeded)
        setLocalOperationInProgress(false)
      }
    },
    [
      actualOwnerEmail,
      audioRegions,
      config,
      createAnnotation,
      currentAnnotation,
      currentRegionsOverride,
      currentUserEmail,
      durationSeconds,
      localOperationInProgress,
      metricId,
      metricOutput,
      onSaveError,
      onSaveStarted,
      projectId,
      simulationOutputId,
      updateAnnotation,
    ],
  )

  const handleClearReview = useCallback(async () => {
    if (!config) {
      return true
    }
    if (!simulationOutputId || !metricId || !currentAnnotation?.id) {
      return false
    }
    if (localOperationInProgress) {
      return true
    }

    setLocalOperationInProgress(true)
    const endSave = onSaveStarted?.()
    let saveSucceeded = false
    try {
      const existingSubvalues = getSubvaluesFromAnnotation(currentAnnotation)
      const subvaluesWithoutThisCategory = existingSubvalues.filter(
        (s) =>
          s.category !== config.category && s.output_type !== config.category,
      )

      await updateAnnotation.mutateAsync({
        annotationId: currentAnnotation.id,
        groundTruthFloatValue: null,
        groundTruthStringValue: null,
        groundTruthSetValue: null,
        groundTruthSubvaluesByTimestamp:
          subvaluesWithoutThisCategory.length > 0
            ? subvaluesWithoutThisCategory
            : null,
        groundTruthJson: null,
        ...(projectId !== undefined ? { projectId } : {}),
      })
      saveSucceeded = true
      return true
    } catch (error) {
      log.error("Unexpected error clearing audio region review", error)
      onSaveError?.(simulationOutputId, error)
      return false
    } finally {
      endSave?.(saveSucceeded)
      setLocalOperationInProgress(false)
    }
  }, [
    config,
    currentAnnotation,
    localOperationInProgress,
    metricId,
    onSaveError,
    onSaveStarted,
    projectId,
    simulationOutputId,
    updateAnnotation,
  ])

  // When config is null, return no-ops
  if (!config) {
    return {
      handleSaveRegions: NOOP_SAVE as (
        regionsAtSaveTime?: AudioRegionParams[] | null,
        overrideFloatValue?: number,
      ) => Promise<boolean>,
      handleClearReview: NOOP_SAVE,
      isUpdating: false,
      audioRegions,
      savedRegionsForMetric: EMPTY_REGIONS,
      reviewAnnotation: null,
    }
  }

  return {
    handleSaveRegions,
    handleClearReview,
    isUpdating: isUpdating || localOperationInProgress,
    audioRegions,
    savedRegionsForMetric,
    /** The reviewer's row as this editor last saved or cleared it. */
    reviewAnnotation: currentAnnotation,
  }
}

// ---------------------------------------------------------------------------
// Shared ground-truth display component for the assignments table
// ---------------------------------------------------------------------------

export const AudioRegionGroundTruthDisplay: React.FC<{
  annotationsForMetric: Annotation[]
  metricRecord: MetricRecord
}> = memo(({ annotationsForMetric, metricRecord }) => {
  const myAnnotation = useReviewSubjectAnnotation(annotationsForMetric)

  const reviewedScore = myAnnotation?.ground_truth_float_value
  if (reviewedScore == null) {
    return (
      <Tooltip content="Edit timeline to show review score.">
        <span
          role="button"
          tabIndex={0}
          aria-label="No review score yet. Edit timeline to show review score."
          className="cursor-default rounded min-h-[24px] min-w-11 px-2 py-1 text-xs font-medium inline-flex items-center justify-center shrink-0 bg-surface-1 text-coval-text-muted"
        >
          <AudioLines className="w-4 h-4" />
        </span>
      </Tooltip>
    )
  }

  return (
    <div className="w-full flex items-center justify-center text-center">
      {metricRecord.renderValueOnlyTableCell(reviewedScore, "COMPLETED", "")}
    </div>
  )
})
AudioRegionGroundTruthDisplay.displayName = "AudioRegionGroundTruthDisplay"
